
import moment from 'moment';
import { action, observable } from 'mobx';
import {
	Model,
	IModelAttributes,
	attribute,
	entity,
	ReferencePath,
} from 'Models/Model';
import * as Models from 'Models/Entities';
import * as Validators from 'Validators';
import { CRUD } from '../CRUDOptions';
import * as AttrUtils from 'Util/AttributeUtils';
import { IAcl } from 'Models/Security/IAcl';
import {
	makeEnumFetchFunction,
	getCreatedModifiedCrudOptions,
} from 'Util/EntityUtils';
import UserDiscountEntity from 'Models/Security/Acl/UserDiscountEntity';
import VisitorsDiscountEntity from 'Models/Security/Acl/VisitorsDiscountEntity';
import * as Enums from '../Enums';
import { EntityFormMode } from 'Views/Components/Helpers/Common';
import SuperAdministratorScheme from '../Security/Acl/SuperAdministratorScheme';

import OrganisationEntity from './OrganisationEntity';
import { store } from 'Models/Store';
import gql from 'graphql-tag';
import { buildSearchConditions } from 'Util/GraphQLUtils';
import { IWhereCondition } from 'Views/Components/ModelCollection/ModelQuery';

export interface IDiscountEntityAttributes extends IModelAttributes {
	source: Enums.discountSource;
	expirationDate: Date;
	discountPercent: number;

	organisationId?: string;
	organisation?: Models.OrganisationEntity | Models.IOrganisationEntityAttributes;

}

@entity('DiscountEntity', 'Discount')
export default class DiscountEntity extends Model
	implements IDiscountEntityAttributes {
	public static acls: IAcl[] = [
		new SuperAdministratorScheme(),
		new UserDiscountEntity(),
		new VisitorsDiscountEntity(),

	];

	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];

	
	/**
	 * The source of the discount
	 */
	@observable
	@attribute<DiscountEntity, Enums.discountSource>()
	@CRUD({
		name: 'Source',
		displayType: 'enum-combobox',
		order: 10,
		headerColumn: true,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: (attr: string) => {
			return AttrUtils.standardiseEnum(attr, Enums.discountSourceOptions);
		},
		enumResolveFunction: makeEnumFetchFunction(Enums.discountSourceOptions),
		displayFunction: (attr: Enums.discountSource) => Enums.discountSourceOptions[attr],
	})
	public source: Enums.discountSource;
	
	/**
	 * Discount apply from the moment they are created until their expiration date
	 */
	@observable
	@attribute<DiscountEntity, Date>({ serialise: (e, a) => AttrUtils.serialiseDate(a) })
	@CRUD({
		name: 'Expiration Date',
		displayType: 'datepicker',
		order: 20,
		headerColumn: true,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: attr => AttrUtils.standardiseDate(attr, { serialiseAs: 'date' }),
	})
	public expirationDate: Date;
	
	@Validators.Integer()
	@observable
	@attribute<DiscountEntity, number>()
	@CRUD({
		name: 'Discount Percent',
		displayType: 'textfield',
		order: 30,
		headerColumn: true,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: AttrUtils.standardiseInteger,
	})
	public discountPercent: number;
	

	/**
	 * An organisation can have discounts
	 */
	@observable
	@attribute()
	@CRUD({
		
		name: 'Organisation',
		displayType: 'reference-combobox',
		order: 40,
		headerColumn: true,
		searchable: true,
		searchFunction: 'like',
		searchTransform: attr => {
			return {
				query: `%${attr}%`,
				extraOptions: {
					case: 'INVARIANT_CULTURE_IGNORE_CASE',
					path: 'organisation.primaryBusinessEntity.name',
				},
			};
		},
		referenceTypeFunc: () => Models.OrganisationEntity,
		referenceResolveFunction: async (search, options) => {
			const searchConditions: IWhereCondition<string>[][] = [];
			if (!!search && typeof search === 'string') {
				searchConditions.push(
					buildSearchConditions(['primaryBusinessEntity.name'], search, 'INVARIANT_CULTURE_IGNORE_CASE'),
				);
			}
			const orderBy = [{ path: 'primaryBusinessEntity.name', descending: false }];
			const { data } = await store.apolloClient.query({
				query: gql`
				query organisationEntitys(
					$orderBy: [OrderByGraph],
					$conditions: [[WhereExpressionGraph]]
				) {
					organisationEntitys(conditions: $conditions, orderBy: $orderBy) {
						id
						primaryBusinessEntity {
							id
							name
						}
					}
				}
				`,
				variables: {
					orderBy: orderBy,
					conditions: searchConditions,
				},
				fetchPolicy: 'network-only',
			});

			return data.organisationEntitys
				.map((org: any) => new OrganisationEntity(org))
				.map((org: OrganisationEntity) => ({ display: org.primaryBusinessEntity?.name, value: org }));
		},
		optionEqualFunc: (modelProperty, option) => {
			return modelProperty.id === option;
		},
		// @ts-ignore
		displayFunction: (attr, that) => that.organisation?.primaryBusinessEntity?.name,
		
	})
	public organisationId?: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public organisation: Models.OrganisationEntity;



	// eslint-disable-next-line @typescript-eslint/no-useless-constructor
	constructor(attributes?: Partial<IDiscountEntityAttributes>) {
		super(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	@action
	public assignAttributes(attributes?: Partial<IDiscountEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.source !== undefined) {
				this.source = attributes.source;
			}
			if (attributes.expirationDate !== undefined) {
				if (attributes.expirationDate === null) {
					this.expirationDate = attributes.expirationDate;
				} else {
					this.expirationDate = moment(attributes.expirationDate).toDate();
				}
			}
			if (attributes.discountPercent !== undefined) {
				this.discountPercent = attributes.discountPercent;
			}
			if (attributes.organisationId !== undefined) {
				this.organisationId = attributes.organisationId;
			}
			if (attributes.organisation !== undefined) {
				if (attributes.organisation === null) {
					this.organisation = attributes.organisation;
				} else if (attributes.organisation instanceof Models.OrganisationEntity) {
					this.organisation = attributes.organisation;
					this.organisationId = attributes.organisation.id;
				} else {
					this.organisation = new Models.OrganisationEntity(attributes.organisation);
					this.organisationId = this.organisation.id;
				}
			}

		}
	}

	/**
	 * Additional fields that are added to GraphQL queries when using the
	 * the managed model APIs.
	 */
	public defaultExpands = `
		organisation {
			${Models.OrganisationEntity.getAttributes().join('\n')}
			${Models.OrganisationEntity.getFiles().map(f => f.name).join('\n')}
		}
	`;

	/**
	 * The save method that is called from the admin CRUD components.
	 */
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	public async saveFromCrud(formMode: EntityFormMode) {
		const relationPath: ReferencePath = {
		};
		return this.save(
			relationPath,
			{
				options: [
					{
						key: 'mergeReferences',
						graphQlType: '[String]',
						value: [
						],
					},
				],
			},
		);
	}

	/**
	 * Returns the string representation of this entity to display on the UI.
	 */
	public getDisplayName() {
		// % protected region % [Customise the display name for this entity] on begin
		return `${this.discountPercent}% (${this.source})`;
		// % protected region % [Customise the display name for this entity] end
	}

	public listExpands = `
		organisation {
			id
			primaryBusinessEntity {
				id
				name
			}
		}
	`;
}

/*
 * Retrieve the created and modified CRUD attributes for defining the CRUD views and decorate the class with them.
 */
const [createdAttr, modifiedAttr] = getCreatedModifiedCrudOptions();
CRUD(createdAttr)(DiscountEntity.prototype, 'created');
CRUD(modifiedAttr)(DiscountEntity.prototype, 'modified');
